import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";

const CONTAINER = { width: "100%", height: "100%" };
const DEFAULT_CENTER = { lat: -25.2744, lng: 133.7751 };
const DEFAULT_ZOOM = 5;

const MAP_OPTIONS = {
  streetViewControl: false,
  mapTypeControl: false,
  fullscreenControl: true,
  zoomControl: true,
  gestureHandling: "greedy",
  minZoom: 2,
};

const personIconUrl = (color, size) => {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="${size}" height="${size}">
    <circle cx="12" cy="12" r="11" fill="${color}" stroke="white" stroke-width="1.5"/>
    <circle cx="12" cy="9" r="3.5" fill="white"/>
    <path d="M5 19.5c0-3.5 3.1-6 7-6s7 2.5 7 6" fill="white"/>
  </svg>`;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const fitBoundsToEngineers = (map, pts) => {
  if (!pts.length) return;
  const bounds = new window.google.maps.LatLngBounds();
  pts.forEach((e) => bounds.extend({ lat: e.lat, lng: e.lng }));
  map.fitBounds(bounds, 60);
};

const EngineerMapView = ({ engineers = [], selected, onSelect }) => {
  const mapRef = useRef(null);
  const wrapperRef = useRef(null);
  const hoverTimer = useRef(null);
  const [icons, setIcons] = useState(null);
  const [tooltip, setTooltip] = useState(null); // { eng, x, y }

  const onLoad = useCallback((map) => {
    mapRef.current = map;
    const make = (color, size) => ({
      url: personIconUrl(color, size),
      scaledSize: new window.google.maps.Size(size, size),
      anchor: new window.google.maps.Point(size / 2, size / 2),
    });
    setIcons({ default: make("#4F7FEF", 32), selected: make("#4338CA", 40) });
  }, []);

  useEffect(() => {
    if (selected?.lat && selected?.lng && mapRef.current) {
      mapRef.current.panTo({ lat: selected.lat, lng: selected.lng });
      if (mapRef.current.getZoom() < 8) mapRef.current.setZoom(8);
    }
  }, [selected]);

  useEffect(() => {
    if (!mapRef.current) return;
    const pts = engineers.filter((e) => e.lat && e.lng);
    if (pts.length > 0) fitBoundsToEngineers(mapRef.current, pts);
  }, [engineers]);

  const handleMouseOver = useCallback((eng, gEvent) => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
    if (gEvent?.domEvent && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const x = (gEvent.domEvent.clientX ?? gEvent.domEvent.touches?.[0]?.clientX ?? 0) - rect.left;
      const y = (gEvent.domEvent.clientY ?? gEvent.domEvent.touches?.[0]?.clientY ?? 0) - rect.top;
      setTooltip({ eng, x, y });
    }
  }, []);

  const handleMouseOut = useCallback(() => {
    hoverTimer.current = setTimeout(() => setTooltip(null), 100);
  }, []);

  const mapped = useMemo(() => engineers.filter((e) => e.lat && e.lng), [engineers]);

  return (
    <div ref={wrapperRef} style={{ position: "relative", width: "100%", height: "100%" }}>
      <GoogleMap
        mapContainerStyle={CONTAINER}
        center={DEFAULT_CENTER}
        zoom={DEFAULT_ZOOM}
        onLoad={onLoad}
        options={MAP_OPTIONS}
      >
        {icons &&
          mapped.map((eng) => (
            <Marker
              key={eng.id}
              position={{ lat: eng.lat, lng: eng.lng }}
              icon={selected?.id === eng.id ? icons.selected : icons.default}
              zIndex={selected?.id === eng.id ? 10 : 1}
              onClick={() => onSelect?.(eng)}
              onMouseOver={(e) => handleMouseOver(eng, e)}
              onMouseOut={handleMouseOut}
            />
          ))}
      </GoogleMap>

      {tooltip && (
        <div
          style={{
            position: "absolute",
            left: tooltip.x,
            top: tooltip.y - 16,
            transform: "translate(-50%, -100%)",
            pointerEvents: "none",
            zIndex: 9999,
            background: "rgba(15,23,42,0.92)",
            color: "#fff",
            borderRadius: 8,
            padding: "6px 12px",
            fontSize: 12,
            fontWeight: 600,
            whiteSpace: "nowrap",
            boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
            backdropFilter: "blur(6px)",
          }}
        >
          {tooltip.eng.name}
          {(tooltip.eng.city || tooltip.eng.country) && (
            <span style={{ fontWeight: 400, opacity: 0.65, marginLeft: 6 }}>
              {[tooltip.eng.city, tooltip.eng.country].filter(Boolean).join(", ")}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default EngineerMapView;
